import type { LlmEntry } from "../config";
import type { StructuredResult } from "../types";
import { friendlyLabel, replaceIdsInText } from "./llmLabels";

function fmtConfidence(c: number | undefined): string {
  if (typeof c !== "number") return "—";
  if (c === -1) return "abstain";
  return `${(c * 100).toFixed(1)}%`;
}

export type ExportInput = {
  prompt: string;
  method: string;
  deliberative: boolean;
  selectedLlms: string[];
  result: StructuredResult | string | null | undefined;
  timestamp?: Date;
  availableLlms: LlmEntry[];
};

export function toPlainText(x: ExportInput): string {
  const ts = (x.timestamp ?? new Date()).toISOString();
  const meta = new Map(x.availableLlms.map((l) => [l.id, l]));
  const knownIds = x.availableLlms.map((l) => l.id);
  const label = (id: string) => friendlyLabel(id, meta);
  const lines: string[] = [];
  lines.push(`MAGI Deliberation Report`);
  lines.push(`Timestamp : ${ts}`);
  lines.push(`Method    : ${x.method}${x.deliberative ? " (deliberative)" : ""}`);
  lines.push(`Models    : ${x.selectedLlms.map(label).join(", ") || "(auto)"}`);
  lines.push("");
  lines.push("Question:");
  lines.push(x.prompt);
  lines.push("");
  lines.push("--------------------------------");

  if (typeof x.result === "string") {
    lines.push(x.result);
    return lines.join("\n");
  }
  const r = x.result;
  if (!r || !Array.isArray(r.rounds)) {
    lines.push("(no result)");
    return lines.join("\n");
  }

  r.rounds.forEach((round, i) => {
    lines.push("");
    lines.push(`Round ${i + 1}`);
    lines.push("--------");
    round.responses.forEach((resp) => {
      lines.push(
        `- ${label(resp.model)} confidence=${fmtConfidence(resp.confidence_score)}`,
      );
      lines.push(`  Response: ${String(resp.response ?? "")}`);
      if (resp.reason) lines.push(`  Reason  : ${resp.reason}`);
    });
    if (round.aggregate) {
      lines.push("");
      lines.push("Aggregate:");
      lines.push(JSON.stringify(round.aggregate, null, 2));
    }
    if (round.rapporteur) {
      lines.push("");
      lines.push(`Rapporteur (${label(round.rapporteur.model)}):`);
      lines.push(replaceIdsInText(round.rapporteur.summary, knownIds, label));
    }
  });

  if (r.errors && r.errors.length > 0) {
    lines.push("");
    lines.push("Errors:");
    for (const e of r.errors) lines.push(`- ${label(e.model)}: ${e.error}`);
  }
  return lines.join("\n");
}

export function toMarkdown(x: ExportInput): string {
  const ts = (x.timestamp ?? new Date()).toISOString();
  const meta = new Map(x.availableLlms.map((l) => [l.id, l]));
  const knownIds = x.availableLlms.map((l) => l.id);
  const label = (id: string) => friendlyLabel(id, meta);
  const parts: string[] = [];
  parts.push(`# MAGI Deliberation Report`);
  parts.push("");
  parts.push(`- **Timestamp:** ${ts}`);
  parts.push(`- **Method:** \`${x.method}\`${x.deliberative ? " (deliberative)" : ""}`);
  parts.push(`- **Models:** ${x.selectedLlms.map(label).join(", ") || "_auto_"}`);
  parts.push("");
  parts.push(`## Question`);
  parts.push("");
  parts.push("> " + x.prompt.replace(/\n/g, "\n> "));
  parts.push("");

  if (typeof x.result === "string") {
    parts.push("## Result");
    parts.push("");
    parts.push(x.result);
    return parts.join("\n");
  }
  const r = x.result;
  if (!r || !Array.isArray(r.rounds)) {
    parts.push("_No result._");
    return parts.join("\n");
  }

  r.rounds.forEach((round, i) => {
    parts.push(`## Round ${i + 1}`);
    parts.push("");
    parts.push("| Model | Confidence | Response |");
    parts.push("|---|---|---|");
    for (const resp of round.responses) {
      const short = String(resp.response ?? "").replace(/\|/g, "\\|").slice(0, 200);
      parts.push(
        `| ${label(resp.model)} | ${fmtConfidence(resp.confidence_score)} | ${short} |`,
      );
    }
    parts.push("");
    for (const resp of round.responses) {
      if (resp.reason) {
        parts.push(`**${label(resp.model)} — reasoning.** ${resp.reason}`);
        parts.push("");
      }
    }
    if (round.aggregate) {
      parts.push("### Aggregate");
      parts.push("");
      parts.push("```json");
      parts.push(JSON.stringify(round.aggregate, null, 2));
      parts.push("```");
      parts.push("");
    }
    if (round.rapporteur) {
      parts.push(`### Rapporteur — ${label(round.rapporteur.model)}`);
      parts.push("");
      parts.push(replaceIdsInText(round.rapporteur.summary, knownIds, label));
      parts.push("");
    }
  });

  if (r.errors && r.errors.length > 0) {
    parts.push("## Errors");
    for (const e of r.errors) parts.push(`- ${label(e.model)}: ${e.error}`);
  }
  return parts.join("\n");
}

export function downloadFile(filename: string, contents: string, mime: string) {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

export function safeFilename(prompt: string, ext: string): string {
  const stem =
    prompt
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9\-_]/g, "")
      .slice(0, 40) || "deliberation";
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `magi-${stem}-${stamp}.${ext}`;
}
